import { format } from "date-fns";
import type {
	ClientPortfolioManualValueResponseDTO,
	ClientPortfolioManualValueSaveRequestDTO,
} from "web_myplann/Infrastructure/Api/Api";
import type { ManualValueFormOutput } from "web_myplann/Portfolio/PortfolioShared/ManualValue/manualValueFormTypes";
import { mapManualValueToRequest } from "web_myplann/Portfolio/PortfolioShared/ManualValue/mapManualValueToRequest";
import { sortManualValuesNewestFirst } from "web_myplann/Portfolio/PortfolioShared/ManualValue/sortManualValuesNewestFirst";
import type { ToolPreviousValue } from "web_myplann/Portfolio/PortfolioShared/ManualValue/toolPreviousValueSchema";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type ToolManualValueRequest = ClientPortfolioManualValueSaveRequestDTO & {
	date: string;
};

type ToolManualValuesForm = ManualValueFormOutput & {
	valueId: number | null;
	previousValues: ToolPreviousValue[];
};

const toComparable = (manualValues: ToolManualValueRequest[]) =>
	JSON.stringify(
		sortManualValuesNewestFirst(manualValues).map(
			({ id, value, invested, date }) => [
				id ?? null,
				value,
				invested ?? null,
				date,
			],
		),
	);

export const mapToolManualValuesToRequest = (
	tool: ToolManualValuesForm,
	original: ClientPortfolioManualValueResponseDTO[],
): ToolManualValueRequest[] | undefined => {
	const current = mapManualValueToRequest(tool);
	const currentDate = current?.date ?? format(new Date(), DateFormat.Iso);
	const originalCurrent = original.find(({ id }) => id === tool.valueId);
	const isCurrentUnchanged =
		current !== undefined &&
		originalCurrent !== undefined &&
		current.value === originalCurrent.value &&
		(current.invested ?? null) === (originalCurrent.invested ?? null) &&
		currentDate === originalCurrent.date;
	const manualValues: ToolManualValueRequest[] = [
		...tool.previousValues,
		...(current
			? [
					{
						...current,
						id: isCurrentUnchanged ? tool.valueId : null,
						date: currentDate,
					},
				]
			: []),
	];

	return toComparable(manualValues) === toComparable(original)
		? undefined
		: manualValues;
};
